import { Controller } from "@hotwired/stimulus"

// Only one preview may sound at a time — module scope, so every instance on the
// page shares it. Starting a preview stops whichever was already running.
let currentlyPlaying = null

// Play/stop preview for an audio-track select.
//
// The URL comes from the selected <option>'s data-url. The blank "Template
// default" option has no option of its own to hang a URL on, so the resolved
// default's URL sits on the controller element instead — previewing the default
// is the main thing you want before deciding to override it.
//
//   <div data-controller="audio-preview" data-audio-preview-default-url-value="…">
//     <button data-action="audio-preview#toggle" data-audio-preview-target="button">
//       <i data-audio-preview-target="icon"></i>
//     </button>
//     <select data-audio-preview-target="select"
//             data-action="change->audio-preview#change">…</select>
//   </div>
export default class extends Controller {
  static targets = ["select", "button", "icon"]
  static values = { defaultUrl: String }

  connect() {
    this.audio = null
    this.refresh()
  }

  disconnect() {
    this.stop()
  }

  toggle() {
    if (this.audio) {
      this.stop()
      return
    }

    const url = this.currentUrl()
    if (!url) return

    if (currentlyPlaying && currentlyPlaying !== this) currentlyPlaying.stop()

    this.audio = new Audio(url)
    this.audio.addEventListener("ended", () => this.stop())
    this.audio.addEventListener("error", () => this.stop())
    this.audio.play().catch(() => this.stop())

    currentlyPlaying = this
    this.refresh()
  }

  // Changing the selection while a preview is sounding would leave you hearing
  // one track and looking at another, so stop first.
  change() {
    this.stop()
    this.refresh()
  }

  stop() {
    if (this.audio) {
      this.audio.pause()
      this.audio = null
    }
    if (currentlyPlaying === this) currentlyPlaying = null
    this.refresh()
  }

  currentUrl() {
    if (!this.hasSelectTarget) return this.defaultUrlValue

    const option = this.selectTarget.selectedOptions[0]
    // A blank value is the "template default" row; pool options resolve at
    // render time and so have nothing to play.
    return option?.dataset.url || (option?.value ? "" : this.defaultUrlValue)
  }

  refresh() {
    const playable = Boolean(this.currentUrl())

    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = !playable
      this.buttonTarget.classList.toggle("opacity-40", !playable)
      this.buttonTarget.classList.toggle("cursor-not-allowed", !playable)
      this.buttonTarget.title = playable
        ? this.audio
          ? "Stop"
          : "Preview"
        : "Nothing to preview"
    }

    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle("fa-play", !this.audio)
      this.iconTarget.classList.toggle("fa-stop", Boolean(this.audio))
    }
  }
}
